// src/context/auth-context.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "./ThemeContext";

const REMEMBER_KEY = "eva_remember_me";
const SESSION_CHECK_INTERVAL_MS = 10000; // 10s

export type Sessao = {
  id: number;
  nome: string;
  email?: string;
  picture?: string | number | null;
  departamento?: string | null;
  funcao?: string | null;
  empresa_id?: number | null;
};

type AuthContextValue = {
  loading: boolean;
  sessao: Sessao | null;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [sessao, setSessao] = useState<Sessao | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hadValidSession, setHadValidSession] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { setTheme, setSystemTitle } = useTheme();

  // lê o "lembrar de mim" apenas 1x
  const rememberMe = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(REMEMBER_KEY) || "false") === true;
    } catch {
      return false;
    }
  }, []);

  const clearRemember = useCallback(() => {
    if (!rememberMe) {
      try {
        localStorage.removeItem(REMEMBER_KEY);
      } catch {}
    }
  }, [rememberMe]);

  /** Aplica config (theme e systemTitle) vinda da API, se existir */
  const applyConfigFromApi = useCallback(
    (config: any) => {
      if (!config) return;

      // theme (JSON)
      if (config.theme) {
        try {
          setTheme(config.theme);
        } catch {
          // ignora se vier algo estranho
        }
      }

      // systemTitle pode ser string ou array
      const stRaw = config.systemTitle;
      let st: string | null = null;
      if (Array.isArray(stRaw)) {
        st = stRaw.join(" • ");
      } else if (typeof stRaw === "string") {
        st = stRaw;
      }
      if (st) {
        setSystemTitle(st);
      }
    },
    [setTheme, setSystemTitle]
  );

  /**
   * refresh(): chama /auth/me
   * - se sucesso → salva sessão e marca hadValidSession=true
   * - se 401/403:
   *     - se NUNCA teve sessão → apenas zera sessão e deixa usuário no /entrar
   *     - se JÁ teve sessão → redireciona para /sessao-encerrada (se não estiver lá nem no /entrar)
   */
  const refresh = useCallback(
    async (fromInterval: boolean = false) => {
      // não mostra loading no ping periódico pra não ficar piscando
      if (!fromInterval) {
        setLoading(true);
        setError(null);
      }

      try {
        const r = await fetch("https://api.eva-googlemarine.com/auth/me", {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        });

        if (!r.ok) {
          // 401 / 403 / etc
          clearRemember();
          setSessao(null);

          // se nunca teve sessão válida, não é "sessão encerrada", é só "não logado"
          if (hadValidSession) {
            const path = location.pathname;
            const isOnLogin = path === "/entrar" || path === "/login";
            const isOnSessaoEncerrada = path === "/sessao-encerrada";

            if (!isOnLogin && !isOnSessaoEncerrada) {
              navigate("/sessao-encerrada", { replace: true });
            }
          }

          return;
        }

        const data = await r.json().catch(() => null);

        if (data?.ok && data?.sessao?.nome) {
          const s: Sessao = {
            id: data.sessao.id ?? data.sessao.uid ?? 0,
            nome: data.sessao.nome,
            email: data.sessao.email ?? undefined,
            picture: data.sessao.picture ?? null,
            departamento:
              typeof data.sessao.departamento === "string"
                ? String(data.sessao.departamento)
                : null,
            funcao:
              typeof data.sessao.funcao === "string"
                ? String(data.sessao.funcao)
                : null,
            empresa_id:
              typeof data.sessao.empresa_id === "number"
                ? data.sessao.empresa_id
                : null,
          };
          setSessao(s);
          setHadValidSession(true);

          // aplica config (theme, systemTitle)
          applyConfigFromApi(data.config || null);
        } else {
          clearRemember();
          setSessao(null);
        }
      } catch (e: any) {
        if (!fromInterval) {
          setError(e?.message || "Erro ao carregar sessão");
        }
        setSessao(null);
      } finally {
        if (!fromInterval) {
          setLoading(false);
        }
      }
    },
    [
      applyConfigFromApi,
      clearRemember,
      hadValidSession,
      location.pathname,
      navigate,
    ]
  );

  const logout = useCallback(async () => {
    try {
      await fetch("https://api.eva-googlemarine.com/auth/logout", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      }).catch(() => null);
    } finally {
      try {
        localStorage.removeItem(REMEMBER_KEY);
        localStorage.removeItem("access_token");
      } catch {}
      setSessao(null);
      setHadValidSession(false);
      // volta pro login
      window.location.assign("/entrar");
    }
  }, []);

  // carrega /auth/me uma vez quando o app sobe
  useEffect(() => {
    refresh(false);
  }, [refresh]);

  // ping periódico pra detectar "logout remoto" (login em outro computador)
  useEffect(() => {
    const id = window.setInterval(() => {
      refresh(true);
    }, SESSION_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(id);
    };
  }, [refresh]);

  const value: AuthContextValue = {
    loading,
    sessao,
    error,
    refresh: () => refresh(false),
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve ser usado dentro de <AuthProvider>");
  }
  return ctx;
}
